/**
 * pdp-v2-video-facade.js — Video Facade Web Component
 * ======================================================
 * Renders a thumbnail button in place of a video iframe.
 * On click, swaps the button for an autoplay iframe.
 *
 * This "facade" pattern prevents YouTube and Vimeo from loading their
 * heavy scripts on initial page load, protecting mobile LCP scores.
 *
 * The thumbnail image source priority:
 *   1. custom.product_video_thumbnail metafield (explicit override)
 *   2. Derived from YouTube video ID (i.ytimg.com/vi/{ID}/maxresdefault.jpg),
 *      falling back to hqdefault.jpg — see thumbnail fallback note below
 *   3. Vimeo: falls back to a grey placeholder (Vimeo thumb requires an API call)
 *
 * Thumbnail fallback:
 *   YouTube only generates maxresdefault.jpg (1280x720) for videos sourced at
 *   >=720p. For everything else that URL 404s and returns a ~120x90 grey
 *   placeholder image. The thumbnail is rendered as an <img src=maxresdefault
 *   data-thumb-fallback=hqdefault>; on load we swap to the fallback if the image
 *   errored OR decoded to the tell-tale <=120px placeholder. hqdefault.jpg is
 *   generated for every public video, so it is a safe terminal fallback.
 *
 * Autoplay URL:
 *   YouTube: {embed_url}?autoplay=1&rel=0
 *   Vimeo:   {embed_url}?autoplay=1
 *
 * Usage (Liquid):
 *   <pdp-v2-video-facade
 *     data-embed-url="{{ embed_url | escape }}"
 *     data-label="{{ section.settings.subheading | escape }}">
 *     <button class="pdp-v2-video__play-btn"
 *             aria-label="Play video: {{ section.settings.subheading | escape }}">
 *       <img class="pdp-v2-video__thumb" src="{{ thumb_url | escape }}"
 *            data-thumb-fallback="{{ thumb_fallback | escape }}" alt="">
 *       {%- render 'icon-play' -%}
 *     </button>
 *   </pdp-v2-video-facade>
 *
 * Implementation: Phase 5 (Step 5c)
 */

// YouTube serves a ~120x90 grey placeholder when a resolution is unavailable.
// Any decoded thumbnail this small (or smaller) is treated as "missing".
const YT_PLACEHOLDER_MAX_WIDTH = 120;

class PdpV2VideoFacade extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector('button');
    if (btn) {
      btn.addEventListener('click', () => this.loadVideo(), { once: true });
    }

    const thumb = this.querySelector('.pdp-v2-video__thumb');
    if (thumb) {
      this.wireThumbnailFallback(thumb);
    }
  }

  /**
   * Swap the thumbnail to its fallback URL when the primary source fails to
   * load or resolves to YouTube's grey placeholder.
   *
   * Handles the deferred-script race: the <img> may already have finished
   * loading (or errored) before this runs, so we check img.complete up front
   * in addition to attaching load/error listeners.
   */
  wireThumbnailFallback(img) {
    const fallback = img.dataset.thumbFallback;
    if (!fallback) return;

    const applyFallback = () => {
      if (img.getAttribute('src') !== fallback) {
        img.src = fallback;
      }
    };

    // Errored outright (naturalWidth 0) or decoded the tiny grey placeholder.
    const inspect = () => {
      if (img.naturalWidth === 0 || img.naturalWidth <= YT_PLACEHOLDER_MAX_WIDTH) {
        applyFallback();
      }
    };

    if (img.complete) {
      inspect();
    } else {
      img.addEventListener('load', inspect, { once: true });
      img.addEventListener('error', applyFallback, { once: true });
    }
  }

  /**
   * Replace the thumbnail button with a live autoplay iframe.
   */
  loadVideo() {
    const embedUrl = this.dataset.embedUrl;
    if (!embedUrl) return;

    // Append autoplay param (handle both ? and & correctly)
    const autoplayUrl = embedUrl.includes('?')
      ? `${embedUrl}&autoplay=1`
      : `${embedUrl}?autoplay=1`;

    const iframe = document.createElement('iframe');
    iframe.src = autoplayUrl;
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.setAttribute('title', this.dataset.label || 'Product video');
    iframe.className = 'pdp-v2-video__iframe';
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.style.border = 'none';

    this.innerHTML = '';
    this.appendChild(iframe);
  }
}

if (!customElements.get('pdp-v2-video-facade')) {
  customElements.define('pdp-v2-video-facade', PdpV2VideoFacade);
}
